import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Show, SignIn, SignUp } from "@clerk/react";
import { Toaster } from "sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import { Layout } from "@/components/Layout";
import { MeProvider, useMe } from "@/lib/me";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Translate from "@/pages/Translate";
import Studio from "@/pages/Studio";
import Project from "@/pages/Project";
import Discover from "@/pages/Discover";
import LibraryPage from "@/pages/Library";
import Exports from "@/pages/Exports";
import Rights from "@/pages/Rights";
import Billing, { PaymentResult } from "@/pages/Billing";
import Account from "@/pages/Account";
import Help, { PublicReport, Legal } from "@/pages/Help";
import Admin from "@/pages/Admin";
import { ErrorState, Loading } from "@/components/Layout";

const AuthPage = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-center p-16 gap-6 border-r border-[var(--border)]">
      <img src="/songape-logo.png" alt="SONGAPE logo" className="w-40 h-40 object-contain rounded-2xl" />
      <h1 className="text-5xl font-bold">Understand. <span className="grad-text">Create.</span> Discover.</h1>
      <p className="muted max-w-md">One account for Translate, Studio and Discover. Google and email sign-in link to the same account when the email matches.</p>
    </div>
    <div className="flex flex-col items-center justify-center p-4 gap-3"><InAppHint />{children}</div>
  </div>
);

const embedded = () => {
  const ua = navigator.userAgent;
  let framed = false;
  try { framed = window.self !== window.top; } catch { framed = true; }
  return framed || (/iPhone|iPad/.test(ua) && !/Safari\//.test(ua)) || /FBAN|FBAV|Instagram|Line\/|; wv\)/.test(ua);
};

const InAppHint = () => {
  const [copied, setCopied] = useState(false);
  if (!embedded()) return null;
  const url = window.location.origin + window.location.pathname;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); } catch { window.prompt("Copy this link and open it in Safari:", url); } };
  return (
    <div className="card-2 p-3 text-xs max-w-[400px] w-full space-y-2" data-testid="inapp-browser-hint">
      <p><b>Google sign-in needs a normal browser.</b> You're inside another app's viewer, which can't finish Google sign-in. Open this page in Safari or Chrome, or use email below.</p>
      <div className="flex gap-2">
        <a className="btn btn-primary" href={url} target="_blank" rel="noreferrer" data-testid="inapp-open-browser">Open in browser</a>
        <button className="btn" onClick={copy} data-testid="inapp-copy-link">{copied ? "Link copied" : "Copy link"}</button>
      </div>
    </div>
  );
};

function Gate({ children }) {
  const { me, error, reload } = useMe();
  if (error) return <div className="p-6"><ErrorState error={error} onRetry={reload} /></div>;
  if (!me) return <Loading label="Opening your workspace" />;
  return children;
}

const Private = () => (
  <MeProvider>
    <Layout>
      <Gate>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/translate" element={<Translate />} />
          <Route path="/studio" element={<Studio />} />
          <Route path="/studio/:pid" element={<Project />} />
          <Route path="/discover" element={<Discover />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/exports" element={<Exports />} />
          <Route path="/rights" element={<Rights />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/payment/:result" element={<PaymentResult />} />
          <Route path="/account" element={<Account />} />
          <Route path="/help" element={<Help />} />
          <Route path="/legal/:doc" element={<Legal />} />
          <Route path="/report" element={<PublicReport />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<div className="card p-8" data-testid="not-found">Page not found.</div>} />
        </Routes>
      </Gate>
    </Layout>
  </MeProvider>
);

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Toaster theme="dark" position="top-center" richColors />
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp /></AuthPage>} />
          <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          <Route path="/public/report" element={<div className="max-w-2xl mx-auto p-6"><PublicReport /></div>} />
          <Route path="/public/legal/:doc" element={<div className="max-w-3xl mx-auto p-6"><Legal /></div>} />
          <Route path="*" element={<>
            <Show when="signed-in"><Private /></Show>
            <Show when="signed-out"><SignedOutRoutes /></Show>
          </>} />
        </Routes>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}

const ToSignIn = () => {
  const loc = useLocation();
  return <Navigate to={`/sign-in?redirect_url=${encodeURIComponent(window.location.origin + loc.pathname + loc.search)}`} replace />;
};

const SignedOutRoutes = () => (
  <Routes>
    <Route path="/" element={<Landing />} />
    <Route path="*" element={<ToSignIn />} />
  </Routes>
);
